import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db7netgmkl3spn8smasg";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们会尽快与你联系。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    setSubmitting(true);
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，感谢你的反馈！我们会尽快回复你。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(isSpam ? "提交未通过校验，请检查内容后重试。" : serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <form
      id="contactFeedbackForm"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-600">
        无论是内容建议、合作咨询还是问题反馈，我们都欢迎你的来信。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input id="fb-name" name="name" type="text" required placeholder="请输入你的称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input id="fb-email" name="email" type="email" required placeholder="用于接收回复" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select id="fb-subject" name="subject" required defaultValue="" className={inputClass}>
          <option value="" disabled>
            请选择反馈类型
          </option>
          <option value="内容建议">内容建议</option>
          <option value="合作咨询">合作咨询</option>
          <option value="问题反馈">问题反馈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          rows={4}
          maxLength={500}
          placeholder="请描述你的想法或建议（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      <div className="contact-guard-alt" aria-hidden="true">
        <label htmlFor="fb-website-alt">Please leave this field empty</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={submitting ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-line text-base"} />
          </span>
          {submitting ? "提交中…" : "提交反馈"}
        </button>
        <p className="text-xs text-foreground-500">我们将在 1-2 个工作日内回复，请留意邮箱。</p>
      </div>

      {status === "success" && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800 animate-fade-in">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-checkbox-circle-line text-base" />
          </span>
          {feedback}
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800 animate-fade-in">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line text-base" />
          </span>
          {feedback}
        </div>
      )}
    </form>
  );
}